import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  CoreLink,
  LanguageSwitcher,
  RecursiveMenu,
  RecursiveMenuMaxMenu,
} from '@uncinc/react-drupal-core';
import type { MenuItemLinkElementProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';
import { MenuTransitionProvider, useCollapseSubmenus } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/menu-transitions';
import {
  MagicLine,
  Container,
  Lottie,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import { ReactComponent as UncIncLogo } from '../../../../assets/images/UncIncLogo.svg';
import {
  SCREEN_NORMAL,
} from '../../../../config/constants';

import crossLottie from '../../../../assets/lotties/cross.json';
import logoLottie from '../../../../assets/lotties/logo.json';

import './style.scss';

const Logo = React.memo(() => {
  const { t } = useTranslation();

  return (
    <Lottie
      id="logo"
      className="UncIncLogo__interactive"
      data={logoLottie}
      ariaLabel={t('general.logo_aria_label')}
      autoPlay
      loop={false}
      delay={200}
    />
  );
});

const LogoStatic = () => (
  <UncIncLogo className="UncIncLogo__static" />
);

interface MenuButtonProps {
  lottieRef: React.RefObject<any>;
  isOverlayOpen: React.MutableRefObject<boolean>;
}

const MenuButton = React.memo<MenuButtonProps>(({ lottieRef, isOverlayOpen }) => {
  const { t } = useTranslation();

  return (
    <Lottie
      ref={lottieRef}
      id="hamburgerOpen"
      data={crossLottie}
      ariaLabel=""
      autoPlay={false}
      loop={false}
      direction={1}
      onInit={(instance) => {
        instance.addEventListener('complete', () => {
          instance.setDirection(isOverlayOpen.current ? -1 : 1);
        });
      }}
    />
  );
});

interface MagicLineControllerProps {
  initializeFn: () => void;
  setActiveFn: (active: boolean) => void;
}

// useEffect (NOT useLayoutEffect): a layout effect runs before the ancestor
// MagicLine div's ref attaches, so under SSR setInitialMagicLine() would call
// getBoundingClientRect() on a null parent ref and white-screen the app.
// Deps re-run it on route change and when the async menu data arrives.
const MagicLineController = ({ initializeFn, setActiveFn }: MagicLineControllerProps) => {
  const pathname = useSelector((state: any) => state.getIn(['router', 'location', 'pathname'], ''));
  const menuData = useSelector((state: any) => state.getIn(['menu', 'main']));
  const initRef = useRef(initializeFn);
  const setActiveRef = useRef(setActiveFn);

  initRef.current = initializeFn;
  setActiveRef.current = setActiveFn;

  useEffect(() => {
    const hasActive = document.getElementsByClassName('Header__nav-item--active').length > 0;
    setActiveRef.current(hasActive);
    if (hasActive) {
      initRef.current();
    }
  }, [pathname, menuData]);

  return null;
};


interface DesktopNavProps {
  menuId: string;
  toggleOverlayOpen: (close?: boolean) => void;
  navLabel: string;
}

const DesktopNav = ({ menuId, toggleOverlayOpen, navLabel }: DesktopNavProps) => {
  const collapseSubmenus = useCollapseSubmenus();

  return (
    <RecursiveMenuMaxMenu
      menuId={menuId}
      parentMenuItemWrapperClasses="nodisplay"
      menuItemWrapperWrapperClasses=""
      hoverDetectionThreshold={SCREEN_NORMAL}
      navProps={{ 'aria-label': navLabel }}
      onClickFunction={() => {
        collapseSubmenus();
        toggleOverlayOpen(true);
      }}
    />
  );
};

const MenuItemLinkElement: React.FC<MenuItemLinkElementProps> = ({
  url,
  title,
  isOpen,
}) => (
  <Button
    href={url}
    inverted={isOpen}
    className="Header__contactbutton"
  >
    {title}
  </Button>
);


interface HeaderProps {
  isOpen: boolean;
  isOverlayOpen: React.MutableRefObject<boolean>;
  toggleOverlayOpen: (close?: boolean) => void;
  menuButtonRef: React.RefObject<HTMLDivElement>;
  lottieRef: React.RefObject<any>;
}

const Header = ({
  isOpen,
  isOverlayOpen,
  toggleOverlayOpen,
  menuButtonRef,
  lottieRef,
}: HeaderProps) => {
  const { t } = useTranslation();
  const menuId = 'main';
  const lang = useSelector((state: any) => state.getIn(['ui', 'language']));
  const hoverMagicLineRef = useRef(null);
  const setInitialMagicLineRef = useRef(null);

  return (
    <div className="Header">
      <div className="Header__background">
        <Container>
          <CoreLink
            onClick={() => toggleOverlayOpen(true)}
            className="Header__logo"
            ariaLabel={t('general.logo_aria_label')}
            href={`/${lang}`}
          >
            <Logo />
            <LogoStatic />
          </CoreLink>

          <div className="Header__actions">
            <div
              ref={menuButtonRef}
              className="Header__menu show"
            >
              <MenuTransitionProvider>

                <DesktopNav
                  menuId={menuId}
                  toggleOverlayOpen={toggleOverlayOpen}
                  navLabel={t(`menu_label.${menuId}`)}
                />
                <MagicLine activeClassName="Header__nav-item--active">
                  {(
                    setInitialMagicLine: any,
                    hoverMagicLine: any,
                    setActiveItemInNavigation: any,
                  ) => {
                    hoverMagicLineRef.current = hoverMagicLine;
                    setInitialMagicLineRef.current = setInitialMagicLine;

                    return (
                      <MagicLineController
                        initializeFn={setInitialMagicLine}
                        setActiveFn={setActiveItemInNavigation}
                      />
                    );
                  }}
                </MagicLine>
              </MenuTransitionProvider>
            </div>

            <LanguageSwitcher
              languages={{ nl: 'NL', en: 'EN' }}
              hideActive={false}
            />

            <RecursiveMenu
              menuId="secondary"
              maxLevel={1}
              className="Header__contactbutton"
              MenuItemLinkElement={MenuItemLinkElement}
              onClickFunction={() => toggleOverlayOpen()}
              navProps={{ 'aria-label': t('menu_label.secondary') }}
              {...({ isOpen } as object)}
            />

            <button
              type="button"
              className="Header__hamburger"
              aria-expanded={isOpen}
              aria-controls="main-nav-overlay"
              aria-label={isOpen ? t('general.close_menu') : t('general.open_menu')}
              tabIndex={0}
              onClick={() => toggleOverlayOpen()}
            >
              <div className="Header__label" aria-hidden="true">
                <span>{t('general.close')}</span>
                <span>{t('general.menu')}</span>
              </div>
              <MenuButton
                lottieRef={lottieRef}
                isOverlayOpen={isOverlayOpen}
              />
            </button>
          </div>
        </Container>
      </div>
    </div>
  );
};


export default Header;
